// Load the transfer data
import transferData from '../data/transfer-data.json';

// Get all the rosters
export const getAllPlayers = () => {
  return transferData.college_transfers;
};

// Get rosters by position
export const getPlayersByPosition = (position) => {
  if (!position) return [];
  const positionAbbr = position.toUpperCase();
  return transferData.college_transfers.filter(Roster => 
    Roster.position_abbreviation === positionAbbr
  );
};

// Get rosters by status
export const getPlayersByStatus = (status) => {
  if (!status) return [];
  const statusUpper = status.toUpperCase();
  return transferData.college_transfers.filter(Roster => 
    Roster.status === statusUpper
  );
};

// Get rosters by origin college
export const getPlayersByOriginCollege = (college) => {
  if (!college) return [];
  const collegeLower = college.toLowerCase();
  return transferData.college_transfers.filter(Roster => 
    Roster.origin_college_name && Roster.origin_college_name.toLowerCase().includes(collegeLower)
  );
};

// Get rosters by exit college
export const getPlayersByExitCollege = (college) => {
  if (!college) return [];
  const collegeLower = college.toLowerCase();
  return transferData.college_transfers.filter(Roster => 
    Roster.exit_college_name && Roster.exit_college_name.toLowerCase().includes(collegeLower)
  );
};

// Get rosters by star rating
export const getPlayersByStars = (stars) => {
  if (!stars || isNaN(stars)) return [];
  const starsNum = parseInt(stars);
  return transferData.college_transfers.filter(Roster => 
    Roster.stars === starsNum
  );
};

// Get transfer stats
export const getTransferStats = () => {
  const totalPlayers = transferData.college_transfers.length;
  
  // Status counts
  const statusCounts = {
    TRANSFERRED: 0,
    ENTERED: 0,
    INTENT: 0,
    WITHDREW: 0
  };
  
  // Position counts
  const positionCounts = {};
  
  // College counts
  const originCollegeCounts = {};
  const exitCollegeCounts = {};
  
  // Star rating counts
  const starCounts = {
    0: 0,
    1: 0,
    2: 0,
    3: 0, 
    4: 0,
    5: 0
  };
  
  // Populate the counts
  transferData.college_transfers.forEach(Roster => {
    // Status counts
    if (statusCounts[Roster.status] !== undefined) {
      statusCounts[Roster.status]++;
    }
    
    // Position counts
    if (Roster.position_abbreviation) {
      if (!positionCounts[Roster.position_abbreviation]) {
        positionCounts[Roster.position_abbreviation] = 0;
      }
      positionCounts[Roster.position_abbreviation]++;
    }
    
    // Origin college counts
    if (Roster.origin_college_name) {
      if (!originCollegeCounts[Roster.origin_college_name]) {
        originCollegeCounts[Roster.origin_college_name] = 0;
      }
      originCollegeCounts[Roster.origin_college_name]++;
    }
    
    // Exit college counts
    if (Roster.exit_college_name) {
      if (!exitCollegeCounts[Roster.exit_college_name]) {
        exitCollegeCounts[Roster.exit_college_name] = 0;
      }
      exitCollegeCounts[Roster.exit_college_name]++;
    }
    
    // Star rating counts
    if (starCounts[Roster.stars] !== undefined) {
      starCounts[Roster.stars]++;
    }
  });
  
  // Sort position counts
  const sortedPositions = Object.entries(positionCounts)
    .sort(([, countA], [, countB]) => countB - countA)
    .reduce((obj, [key, value]) => {
      obj[key] = value;
      return obj;
    }, {});
    
  // Sort college counts
  const sortedOriginColleges = Object.entries(originCollegeCounts)
    .sort(([, countA], [, countB]) => countB - countA)
    .slice(0, 20)
    .reduce((obj, [key, value]) => {
      obj[key] = value;
      return obj;
    }, {});
    
  const sortedExitColleges = Object.entries(exitCollegeCounts)
    .sort(([, countA], [, countB]) => countB - countA)
    .slice(0, 20)
    .reduce((obj, [key, value]) => {
      obj[key] = value;
      return obj;
    }, {});
  
  return {
    totalPlayers,
    statusCounts,
    positionCounts: sortedPositions,
    originCollegeCounts: sortedOriginColleges,
    exitCollegeCounts: sortedExitColleges,
    starCounts
  };
};

// Get a formatted summary of the transfer data
export const getTransferDataSummary = () => {
  const stats = getTransferStats();
  
  // Format position summary
  const topPositions = Object.entries(stats.positionCounts)
    .slice(0, 5)
    .map(([pos, count]) => `${pos}: ${count} rosters`);
    
  // Format status summary
  const statusSummary = Object.entries(stats.statusCounts)
    .map(([status, count]) => {
      const percentage = Math.round((count / stats.totalPlayers) * 100);
      return `${status}: ${count} rosters (${percentage}%)`;
    });
    
  // Format college summary
  const topOriginColleges = Object.entries(stats.originCollegeCounts)
    .slice(0, 5)
    .map(([college, count]) => `${college}: ${count} rosters`);
    
  const topExitColleges = Object.entries(stats.exitCollegeCounts)
    .slice(0, 5)
    .map(([college, count]) => `${college}: ${count} rosters`);
    
  // Format star rating summary
  const starSummary = Object.entries(stats.starCounts)
    .filter(([stars, count]) => stars > 0)
    .map(([stars, count]) => {
      const percentage = Math.round((count / stats.totalPlayers) * 100);
      return `${stars}-star: ${count} rosters (${percentage}%)`;
    });
  
  return {
    totalPlayers: stats.totalPlayers,
    statusSummary,
    topPositions,
    topOriginColleges,
    topExitColleges,
    starSummary
  };
};

// Export the raw data as well
export const rawTransferData = transferData;